import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from "@mui/material";

import { MonthlyTestResults } from "components";
import { useTranslation } from "_metronic/i18n/language";
import { useDashDsa } from "../../context/DashdsaContext";
import {
  getTranslatedMonths,
  transformData,
} from "../../utils/monthDataTransformer";
import { DataDashType } from "../../dsa-type";
import { LabelsContants } from "app/helpers/constants";
import { useEffect } from "react";

type TestByMonthChartProps = {
  testNamesList: Array<string>;
  getValuesByTestNames: (
    testName: Array<string>,
    year: number,
  ) => Array<DataDashType>;
  years: Array<number>;
};

export default function TestByMonthChart({
  testNamesList,
  getValuesByTestNames,
  years,
}: TestByMonthChartProps): JSX.Element | null {
  const {
    selectedYear,
    handleChangeYear,
    selectedTestNames,
    handleTestNamesChange,
  } = useDashDsa();
  const translate: (key: string) => string = useTranslation();

  useEffect(() => {
    if (!hasValues) {
      const previousYear: number = Math.max(...years);
      handleChangeYear(previousYear);
    }
  }, [years]);

  const data: Array<DataDashType> = getValuesByTestNames(
    selectedTestNames.filter(value => value !== LabelsContants.ALL),
    +selectedYear,
  );

  if (!data || data.length === 0 || Object.keys(data).length === 0) {
    return null;
  }
  const hasValues: boolean =
    data.reduce((acc: number, val: DataDashType) => acc + val.value, 0) > 0;
  const label = translate("labels_dashboard_selectYear");
  const translatedMonths = getTranslatedMonths(translate);
  const chartData = transformData(
    data,
    translatedMonths,
    selectedYear.toString(),
  );

  const handleChange = ({
    target,
  }: SelectChangeEvent<typeof selectedTestNames>): void => {
    const { value } = target;
    handleTestNamesChange(typeof value === "string" ? value.split(",") : value);
  };

  if (!years.includes(selectedYear)) {
    return null;
  }

  function renderFilter(): JSX.Element | null {
    return (
      <div className="bar-graph-filters">
        <FormControl className="multiple-test-filter" sx={{ m: 1 }}>
          <InputLabel id="multiple-checkbox-label">Test</InputLabel>
          <Select
            labelId="multiple-checkbox-label"
            id="multiple-checkbox"
            className="multiple-checkbox-select"
            multiple
            value={selectedTestNames}
            onChange={handleChange}
            input={<OutlinedInput label="Test" />}
            renderValue={selected => selected.join(", ")}
          >
            <MenuItem key={"key-0"} value={LabelsContants.ALL}>
              <Checkbox
                checked={testNamesList.length === selectedTestNames.length}
                className="test-name-checkox"
                color="info"
                name="all"
                onChange={({ target }) =>
                  handleTestNamesChange(target.checked ? testNamesList : [])
                }
              />
              <ListItemText primary={LabelsContants.ALL} />
            </MenuItem>
            {testNamesList.map(name => (
              <MenuItem key={name} value={name}>
                <Checkbox
                  checked={selectedTestNames.includes(name)}
                  className="test-name-checkox"
                  color="info"
                />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {years.length > 0 && (
          <FormControl
            sx={{ m: 1, minWidth: 120 }}
            size="small"
            className="year-filter"
          >
            <InputLabel htmlFor="yearSelect">{label} </InputLabel>
            <Select
              id="yearSelect"
              value={selectedYear}
              label={label}
              onChange={e => handleChangeYear(+e.target.value)}
            >
              {years.map(year => (
                <MenuItem
                  key={year}
                  selected={year === selectedYear}
                  value={year}
                >
                  <ListItemText>{year}</ListItemText>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>
    );
  }

  return (
    <div className={"c-test-month-container"}>
      <MonthlyTestResults
        title={translate("labels_dashboard_testsMonth")}
        data={chartData}
        actionFilterComponent={renderFilter()}
      />
    </div>
  );
}
